import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { Loader2, Plus, Trash2, Upload, X, FileText } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { EditorHeader } from "./EditorHeader";
import { Json } from "@/integrations/supabase/types";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type Document = {
  name: string;
  path: string;
  url: string;
};

type LegalSection = {
  id: string;
  title: string;
  status: string;
  content: string[];
  documents: Document[] | null;
  created_at: string;
};

const transformDocuments = (documents: Json[] | null): Document[] | null => {
  if (!documents) return null;
  return documents.map(doc => {
    if (typeof doc === 'object' && doc !== null) {
      return {
        name: (doc as any).name || '',
        path: (doc as any).path || '',
        url: (doc as any).url || ''
      };
    }
    return {
      name: '',
      path: '',
      url: ''
    };
  });
};

export const LegalEditor = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState<string | null>(null);

  const { data: legalSections, isLoading, refetch } = useQuery({
    queryKey: ['legal-sections'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('legal_sections')
        .select('*')
        .order('title');
      
      if (error) throw error;
      
      return (data || []).map(section => ({
        ...section,
        documents: transformDocuments(section.documents)
      })) as LegalSection[];
    },
  });

  const handleSave = async (e: React.FormEvent<HTMLFormElement>, sectionId: string) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const contentText = String(formData.get('content'));
    // Ensure we have a proper array of strings for the content column
    const contentArray = contentText
      .split('\n')
      .map(line => line.trim())
      .filter(line => line !== '');

    console.log('Saving content array:', contentArray); // Debug log

    const updates = {
      title: String(formData.get('title')),
      status: String(formData.get('status')),
      content: contentArray,
    };

    try {
      const { data, error } = await supabase
        .from('legal_sections')
        .update(updates)
        .eq('id', sectionId)
        .select(); // Add select() to get the updated data

      if (error) {
        console.error('Update error:', error); // Debug log
        throw error;
      }

      console.log('Updated data:', data); // Debug log

      toast({
        title: "Success",
        description: "Legal section has been updated",
      });
      
      // Force a refetch to ensure UI is in sync with DB
      await refetch();
    } catch (error) {
      console.error('Error details:', error); // Debug log
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to update legal section",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAdd = async () => {
    try {
      const { error } = await supabase
        .from('legal_sections')
        .insert({
          title: 'New Legal Section',
          status: 'draft',
          content: ['New content item'],
          documents: [],
        });

      if (error) throw error;

      toast({
        title: "Success",
        description: "New legal section has been added",
      });
      refetch();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to add legal section",
      });
    }
  };

  const handleDelete = async (sectionId: string) => {
    try {
      const { error } = await supabase
        .from('legal_sections')
        .delete()
        .eq('id', sectionId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Legal section has been deleted",
      });
      refetch();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete legal section",
      });
    }
  };

  const handleDeleteFile = async (sectionId: string, filePath: string) => {
    setIsDeleting(sectionId);
    try {
      const { error: storageError } = await supabase.storage
        .from('legal_documents')
        .remove([filePath]);

      if (storageError) throw storageError;

      const section = legalSections?.find(s => s.id === sectionId);
      if (!section?.documents) return;

      const updatedDocuments = section.documents.filter(doc => doc.path !== filePath);

      const { error: dbError } = await supabase
        .from('legal_sections')
        .update({ documents: updatedDocuments })
        .eq('id', sectionId);

      if (dbError) throw dbError;

      toast({
        title: "Success",
        description: "Document deleted successfully",
      });
      refetch();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete document",
      });
    } finally {
      setIsDeleting(null);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>, sectionId: string) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setIsUploading(sectionId);
    try {
      const section = legalSections?.find(s => s.id === sectionId);
      const filePath = `${sectionId}/${file.name}`;

      const { error: uploadError } = await supabase.storage
        .from('legal_documents')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: publicUrlData } = supabase.storage
        .from('legal_documents')
        .getPublicUrl(filePath);

      const newDocument = {
        name: file.name,
        path: filePath,
        url: publicUrlData.publicUrl
      };

      const updatedDocuments = section?.documents ? [...section.documents, newDocument] : [newDocument];

      const { error: updateError } = await supabase
        .from('legal_sections')
        .update({ 
          documents: updatedDocuments,
        })
        .eq('id', sectionId);

      if (updateError) throw updateError;

      toast({
        title: "Success",
        description: "Document uploaded successfully",
      });
      refetch();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to upload document",
      });
    } finally {
      setIsUploading(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <EditorHeader title="Legal Documents Editor" />
      <Button onClick={handleAdd} className="mb-4">
        <Plus className="h-4 w-4 mr-2" />
        Add Legal Section
      </Button>

      <div className="space-y-6">
        {legalSections?.map((section) => (
          <Card key={section.id}>
            <CardContent className="pt-6">
              <form onSubmit={(e) => handleSave(e, section.id)} className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Title</label>
                  <Input
                    name="title"
                    defaultValue={section.title}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium">Status</label>
                  <Select name="status" defaultValue={section.status}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Established">Established</SelectItem>
                      <SelectItem value="Protected">Protected</SelectItem>
                      <SelectItem value="Filed">Filed</SelectItem>
                      <SelectItem value="Implemented">Implemented</SelectItem>
                      <SelectItem value="Compliant">Compliant</SelectItem>
                      <SelectItem value="Signed">Signed</SelectItem>
                      <SelectItem value="In Progress">In Progress</SelectItem>
                      <SelectItem value="Active">Active</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium">Content (one item per line)</label>
                  <Textarea
                    name="content"
                    defaultValue={section.content.join('\n')}
                    className="mt-1"
                    rows={5}
                  />
                </div>
                <div className="flex justify-between items-start">
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          'Save Changes'
                        )}
                      </Button>
                      <div className="relative">
                        <input
                          type="file"
                          id={`file-${section.id}`}
                          className="hidden"
                          onChange={(e) => handleFileUpload(e, section.id)}
                          accept=".pdf,.doc,.docx"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => document.getElementById(`file-${section.id}`)?.click()}
                          disabled={isUploading === section.id}
                        >
                          {isUploading === section.id ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload className="h-4 w-4 mr-2" />
                              Upload Document
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                    {section.documents && section.documents.length > 0 && (
                      <div className="space-y-2">
                        {section.documents.map((doc, index) => (
                          <div key={index} className="flex items-center gap-2 p-2 bg-muted rounded-md">
                            <FileText className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">
                              {doc.name}
                            </span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="ml-auto h-8 w-8 p-0"
                              onClick={() => handleDeleteFile(section.id, doc.path)}
                              disabled={isDeleting === section.id}
                            >
                              {isDeleting === section.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <X className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <Button 
                    type="button"
                    variant="destructive"
                    onClick={() => handleDelete(section.id)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};